/**
 * 数字/金额正式精度配置的共用文案与表单项
 */
import currency from 'currency.js';

export const LEGACY_PRECISION_TIP = '建议使用上方「精度」配置（0~6）；此项仅作历史回显，不可修改';

/** 正式精度输入框说明 */
export const DECIMAL_PRECISION_TIP = '可输入 0~6 的整数；未填写时回退历史精度配置';

/**
 * 将列/控件上的小数精度配置同步到表单 props，供 getPrecision 按统一优先级解析。
 * 优先级：customPrecisionField → decimalPrecision → numberShowType → showColType
 */
export function syncFloatNumberPrecisionProps(source: any, targetProps: Record<string, any>) {
  if (!source || !targetProps) {
    return;
  }
  if (source.customPrecisionField) {
    targetProps.customPrecisionField = source.customPrecisionField;
  }
  if (source.decimalPrecision != null && source.decimalPrecision !== '') {
    targetProps.decimalPrecision = source.decimalPrecision;
  }
  const numberShowType = source.numberShowType ?? source.props?.numberShowType;
  if (numberShowType != null && numberShowType !== '') {
    targetProps.numberShowType = numberShowType;
  }
  if (source.showColType) {
    targetProps.showColType = source.showColType;
  }
}

/**
 * 解析正式精度字段：非负整数视为已配置；不钳制上限，便于后续扩展
 */
export function parseDecimalPrecision(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = typeof value === 'number' ? value : parseInt(String(value), 10);
  if (isNaN(n) || n < 0 || !Number.isInteger(n)) {
    return null;
  }
  return n;
}

const NUMBER_SHOW_TYPE_PRECISION: Record<string, number> = {
  numberOne: 1,
  numberTwo: 2,
  numberThree: 3,
  numberFour: 4,
  numberFive: 5,
};

const SHOW_COL_TYPE_PRECISION: Record<string, number> = {
  number2: 2,
  number3: 3,
  number4: 4,
  number5: 5,
};

/**
 * 解析金额控件已配精度。空配置与历史项「数字」返回 null，走未配封顶规则。
 * 优先级：customPrecisionField → decimalPrecision → numberShowType → showColType
 */
export function resolveConfiguredPricePrecision(source: any, bopConfig?: any): number | null {
  if (!source) {
    return null;
  }
  const customPrecisionField = source.customPrecisionField;
  if (customPrecisionField && bopConfig && bopConfig[customPrecisionField] != null) {
    const customPrecision = parseInt(bopConfig[customPrecisionField], 10);
    if (!isNaN(customPrecision) && customPrecision >= 0) {
      return customPrecision;
    }
  }

  const decimalPrecision = parseDecimalPrecision(source.decimalPrecision);
  if (decimalPrecision !== null) {
    return decimalPrecision;
  }

  const numberShowType = source.numberShowType;
  if (numberShowType && numberShowType in NUMBER_SHOW_TYPE_PRECISION) {
    return NUMBER_SHOW_TYPE_PRECISION[numberShowType];
  }
  if (typeof numberShowType === 'number' && Number.isInteger(numberShowType) && numberShowType >= 0) {
    return numberShowType;
  }

  const showColType = source.showColType;
  if (showColType && SHOW_COL_TYPE_PRECISION[showColType] != null) {
    return SHOW_COL_TYPE_PRECISION[showColType];
  }
  return null;
}

/** 平台金额精度上限；未配精度时按此封顶，不补 0 */
export const PRICE_UNCONFIGURED_MAX_PRECISION = 6;

/** 未配精度：超过上限四舍五入，不足不补 0（1.23 仍为 1.23，1.2345678 为 1.234568） */
export function toUnconfiguredPriceString(val: any): string {
  if (val === null || val === undefined || val === '') {
    return '';
  }
  const n = parseFloat(String(val).replace(/,/g, ''));
  if (isNaN(n)) {
    return '0';
  }
  const formatted = currency(n, {
    precision: PRICE_UNCONFIGURED_MAX_PRECISION,
    symbol: '',
    separator: '',
  }).format();
  if (!formatted.includes('.')) {
    return formatted;
  }
  return formatted.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}


/**
 * 正式精度表单项（配置侧限制 0~6）
 */
export function decimalPrecisionPropField(
  t: (key: string) => string,
  options?: { show?: (domainData: any) => boolean },
) {
  return {
    id: 'decimalPrecision',
    size: 'small',
    type: 'number',
    min: 0,
    max: 6,
    precision: 0,
    label: t('精度'),
    remark: DECIMAL_PRECISION_TIP,
    ...(options?.show ? { show: options.show } : {}),
  };
}
